import React, { useState, useMemo, useEffect, useRef } from "react"
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Spin,
  Upload,
  Dropdown,
} from "antd"
import {
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  MoreOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import { translations } from "../../utils/Translation/staticTranslation"
import { Contacts as ContactsApi } from "../../Apis/contactsApi"
import {
  uploadContactExcelToS3,
  validateExcelFile,
  getContactPublicUrl,
} from "../../utils/contactsS3Upload"
import "./Contacts.css"

const ContactDetailView = ({
  contactList,
  onBack,
  loading = false,
  onContactListUpdate,
  readOnly = false,
  canDelete = true,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.contactsPage || {}
  const isRTL = lang === "ar"
  const isFetchingRef = useRef(false)

  const [contactsData, setContactsData] = useState([])
  const [contactsLoading, setContactsLoading] = useState(false)
  const [showAddContactModal, setShowAddContactModal] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [form] = Form.useForm()
  const [contactPagination, setContactPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [uploadingExcel, setUploadingExcel] = useState(false)
  const [uploadedExcelPaths, setUploadedExcelPaths] = useState([])
  const [excelFileList, setExcelFileList] = useState([])
  const [showUpdateExcelModal, setShowUpdateExcelModal] = useState(false)
  const [updatingExcel, setUpdatingExcel] = useState(false)
  const [updateExcelForm] = Form.useForm()
  const [searchValue, setSearchValue] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const contactFiles = useMemo(() => {
    if (Array.isArray(contactList?.files) && contactList.files.length > 0) {
      return contactList.files
    }
    if (contactList?.excel_file_path) {
      return [{ id: null, file_path: contactList.excel_file_path }]
    }
    return []
  }, [contactList])

  // Keep files list fresh when entering/switching contact group.
  // This avoids API calls on "Add Contact" button click.
  useEffect(() => {
    let isMounted = true
    const refreshContactFiles = async () => {
      if (!contactList?.id || !onContactListUpdate) return
      try {
        const response = await ContactsApi.getContactLists({
          page: 1,
          pageSize: 1000,
        })
        if (!isMounted) return
        const data = response?.data || response
        const latest = data?.results?.find((list) => list.id === contactList.id)
        if (latest) {
          onContactListUpdate({
            ...contactList,
            excel_file_path:
              latest.excel_file_path || contactList.excel_file_path || null,
            files: Array.isArray(latest.files) ? latest.files : [],
            contactsCount:
              latest.total_contact_users ||
              latest.contacts_count ||
              contactList.contactsCount ||
              0,
          })
        }
      } catch (error) {
        console.error("Error refreshing contact files on detail load:", error)
      }
    }
    refreshContactFiles()
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactList?.id])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 600) // 600ms delay

    return () => clearTimeout(timer)
  }, [searchValue])

  // Fetch contacts function - reusable
  const fetchContacts = async () => {
    if (!contactList?.id) return
    if (isFetchingRef.current) return
    isFetchingRef.current = true

    try {
      setContactsLoading(true)
      const response = await ContactsApi.getContactsByContactId(
        contactList.id,
        {
          search: debouncedSearch,
          page: contactPagination.current,
          pageSize: contactPagination.pageSize,
        },
      )

      // Handle response structure - check both response.data and direct response
      const responseData = response?.data || response

      if (responseData?.results && Array.isArray(responseData.results)) {
        const mappedContacts = responseData.results.map((item) => ({
          id: item.id,
          name: item.name || "",
          email: item.email || "",
        }))

        setContactsData(mappedContacts)
        setContactPagination((prev) => ({
          ...prev,
          total: responseData.count || 0,
        }))
      } else {
        // If no results, set empty array
        setContactsData([])
        setContactPagination((prev) => ({
          ...prev,
          total: 0,
        }))
      }
    } catch (error) {
      console.error("Error fetching contacts:", error)
      const currentT = translations[lang]?.contactsPage || {}
      message.error(
        currentT?.error_fetching_contacts || "Error fetching contacts",
      )
      setContactsData([])
    } finally {
      setContactsLoading(false)
      isFetchingRef.current = false
    }
  }

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== undefined) {
      setContactPagination((prev) => ({ ...prev, current: 1 }))
    }
  }, [debouncedSearch])

  // Fetch contacts when contact list is loaded or search/pagination changes
  useEffect(() => {
    // Prevent concurrent API calls
    if (isFetchingRef.current) return
    fetchContacts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contactList?.id,
    contactPagination.current,
    contactPagination.pageSize,
    lang,
    debouncedSearch,
  ])

  const handleAddContact = () => {
    if (readOnly) return
    setEditingContact(null)
    setUploadedExcelPaths([])
    setExcelFileList([])
    form.resetFields()
    setShowAddContactModal(true)
  }

  const handleEditContact = (record) => {
    if (readOnly) return
    setEditingContact(record)
    form.setFieldsValue({
      name: record.name,
      email: record.email,
    })
    setShowAddContactModal(true)
  }

  const handleDeleteContact = async (contactId) => {
    if (readOnly || !canDelete) return
    try {
      setContactsLoading(true)

      // Step 1: Delete the contact
      const deleteResponse = await ContactsApi.deleteContact(contactId)

      if (
        deleteResponse?.status === 200 ||
        deleteResponse?.status === 204 ||
        deleteResponse?.data
      ) {
        message.success(
          t?.contact_deleted_successfully || "Contact deleted successfully",
        )

        // Step 2: Reset the fetching ref to allow immediate refresh
        isFetchingRef.current = false

        // Step 3: Get current pagination state
        const currentPage = contactPagination.current
        const currentPageSize = contactPagination.pageSize

        // Step 4: ALWAYS call GET API immediately after successful deletion to show latest data
        try {
          const refreshResponse = await ContactsApi.getContactsByContactId(
            contactList.id,
            {
              search: debouncedSearch,
              page: currentPage,
              pageSize: currentPageSize,
            },
          )

          const responseData = refreshResponse?.data || refreshResponse

          if (responseData?.results && Array.isArray(responseData.results)) {
            const mappedContacts = responseData.results.map((item) => ({
              id: item.id,
              name: item.name || "",
              email: item.email || "",
            }))

            setContactsData(mappedContacts)
            setContactPagination((prev) => ({
              ...prev,
              total: responseData.count || 0,
            }))

            // If current page is empty and not page 1, go to previous page
            if (mappedContacts.length === 0 && currentPage > 1) {
              setContactPagination((prev) => ({
                ...prev,
                current: prev.current - 1,
              }))
            }
          } else {
            // If no results, check if we need to go to previous page
            if (currentPage > 1) {
              setContactPagination((prev) => ({
                ...prev,
                current: prev.current - 1,
              }))
            } else {
              setContactsData([])
              setContactPagination((prev) => ({
                ...prev,
                total: 0,
              }))
            }
          }
        } catch (fetchError) {
          console.error("Error fetching contacts after delete:", fetchError)
          // Fallback: use fetchContacts function
          await fetchContacts()
        }

        // Step 5: Update contact list overview to refresh total contacts count
        if (onContactListUpdate && contactList) {
          try {
            const contactListResponse = await ContactsApi.getContactLists({
              page: 1,
              pageSize: 1000, // Get all to find the specific one
            })

            const contactListData =
              contactListResponse?.data || contactListResponse
            if (contactListData?.results) {
              const updatedList = contactListData.results.find(
                (list) => list.id === contactList.id,
              )
              if (updatedList) {
                onContactListUpdate({
                  ...contactList,
                  contactsCount:
                    updatedList.total_contact_users ||
                    updatedList.contacts_count ||
                    0,
                })
              }
            }
          } catch (refreshError) {
            console.error("Error refreshing contact list:", refreshError)
          }
        }
      }
    } catch (error) {
      console.error("Error deleting contact:", error)
      message.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          t?.error_deleting_contact ||
          "Error deleting contact",
      )
    } finally {
      setContactsLoading(false)
    }
  }

  const handleDownloadExcel = (filePath) => {
    const pathToDownload = filePath || contactFiles?.[0]?.file_path
    if (!pathToDownload) {
      message.error(t?.excel_file_not_found || "Excel file not found")
      return
    }

    try {
      const fileUrl = getContactPublicUrl(pathToDownload)
      const fileName = pathToDownload.split("/").pop() || "contact_excel.xlsx"

      const link = document.createElement("a")
      link.href = fileUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      message.success(t?.excel_download_started || "Download started")
    } catch (error) {
      console.error("Download error:", error)
      message.error(
        t?.failed_to_download_excel || "Failed to download Excel file",
      )
    }
  }

  const handleUpdateExcel = async (values) => {
    if (readOnly) return
    if (!contactList?.id) {
      message.error(t?.contact_list_id_missing || "Contact list ID is missing")
      return
    }

    try {
      setUpdatingExcel(true)

      // The file should already be uploaded to S3 via beforeUpload
      // We just need to get the uploaded path from the form
      const uploadedPath = uploadedExcelPaths[0] || null

      if (!uploadedPath) {
        message.error(t?.excel_file_required || "Please upload an Excel file")
        return
      }

      // Call PATCH API to update the contact list with the new Excel file path
      // Using the contact list ID in the URL path: PATCH /account/contacts/{id}/
      const updateResponse = await ContactsApi.updateContactList(
        contactList.id,
        {
          file_path: uploadedPath,
        },
      )

      if (
        updateResponse?.status === 200 ||
        updateResponse?.status === 201 ||
        updateResponse?.data
      ) {
        // Reset the fetching ref to allow immediate refresh
        isFetchingRef.current = false

        // ALWAYS call GET API immediately after successful Excel update to show latest data
        try {
          // Refresh contact list details
          const contactListResponse = await ContactsApi.getContactLists({
            page: 1,
            pageSize: 1000, // Get all to find the specific one
          })

          const contactListData =
            contactListResponse?.data || contactListResponse
          if (contactListData?.results) {
            const updatedList = contactListData.results.find(
              (list) => list.id === contactList.id,
            )
            if (updatedList && onContactListUpdate) {
              onContactListUpdate({
                ...contactList,
                excel_file_path: updatedList.excel_file_path || uploadedPath,
                files: Array.isArray(updatedList.files) ? updatedList.files : [],
                contactsCount:
                  updatedList.total_contact_users ||
                  updatedList.contacts_count ||
                  contactList.contactsCount ||
                  0,
              })
            }
          }

          // Refresh contacts list
          await fetchContacts()
        } catch (refreshError) {
          console.error(
            "Error refreshing data after Excel update:",
            refreshError,
          )
          // Update local state as fallback
          if (onContactListUpdate) {
            onContactListUpdate({
              ...contactList,
              excel_file_path: uploadedPath,
              files: [...contactFiles, { id: null, file_path: uploadedPath }],
            })
          }
        }
      }

      message.success(
        t?.excel_updated_successfully || "Excel file updated successfully",
      )
      setShowUpdateExcelModal(false)
      setUploadedExcelPaths([])
      updateExcelForm.resetFields()
    } catch (error) {
      console.error("Error updating Excel file:", error)
      message.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          t?.failed_to_update_excel ||
          "Failed to update Excel file",
      )
    } finally {
      setUpdatingExcel(false)
    }
  }

  const handleDeleteExcel = async (fileItem) => {
    if (readOnly || !canDelete) return
    if (!fileItem?.id) {
      message.error(t?.excel_file_not_found || "Excel file not found")
      return
    }

    const hasMultipleFiles = contactFiles.length > 1
    const fileName = fileItem?.file_path?.split("/").pop() || "this file"

    Modal.confirm({
      title: t?.confirm_delete_excel || "Confirm Delete Excel File",
      content: (
        <div>
          <div>
            {t?.confirm_delete_excel_message ||
              "Are you sure you want to delete this Excel file? This action cannot be undone."}
          </div>
          {hasMultipleFiles && (
            <div style={{ marginTop: "10px", color: "#B42318", fontWeight: 500 }}>
              {t?.duplicate_user_delete_warning ||
                `This sheet may contain users that also exist in other sheets. If you delete "${fileName}", those duplicate users may also be removed from the contact group. Do you want to continue?`}
            </div>
          )}
        </div>
      ),
      okText:
        t?.modal?.delete_with_confirmation || t?.modal?.delete || "Yes, Delete",
      okType: "danger",
      cancelText: t?.modal?.cancel || "Cancel",
      centered: true,
      onOk: async () => {
        try {
          setContactsLoading(true)
          // Delete a single file by contact_file_id
          const deleteResponse = await ContactsApi.deleteExcelFile({
            contact: contactList.id,
            contact_file_id: fileItem.id,
          })

          if (
            deleteResponse?.status === 200 ||
            deleteResponse?.status === 204 ||
            deleteResponse?.data
          ) {
            // Reset the fetching ref to allow immediate refresh
            isFetchingRef.current = false

            // ALWAYS call GET API immediately after successful Excel deletion to show latest data
            try {
              // Refresh contact list details
              const contactListResponse = await ContactsApi.getContactLists({
                page: 1,
                pageSize: 1000, // Get all to find the specific one
              })

              const contactListData =
                contactListResponse?.data || contactListResponse
              if (contactListData?.results) {
                const updatedList = contactListData.results.find(
                  (list) => list.id === contactList.id,
                )
                if (updatedList && onContactListUpdate) {
                  onContactListUpdate({
                    ...contactList,
                    excel_file_path: updatedList.excel_file_path || null,
                    files: Array.isArray(updatedList.files) ? updatedList.files : [],
                    contactsCount:
                      updatedList.total_contact_users ||
                      updatedList.contacts_count ||
                      0,
                  })
                } else if (onContactListUpdate) {
                  // Fallback if not found
                  onContactListUpdate({
                    ...contactList,
                    files: contactFiles.filter((f) => f?.id !== fileItem.id),
                  })
                }
              } else if (onContactListUpdate) {
                // Fallback if API fails
                onContactListUpdate({
                  ...contactList,
                  files: contactFiles.filter((f) => f?.id !== fileItem.id),
                })
              }

              // Refresh the displayed contacts list
              await fetchContacts()
            } catch (refreshError) {
              console.error(
                "Error refreshing data after Excel delete:",
                refreshError,
              )
              // Fallback update
              if (onContactListUpdate) {
                onContactListUpdate({
                  ...contactList,
                  files: contactFiles.filter((f) => f?.id !== fileItem.id),
                })
              }
            }

            message.success(
              t?.excel_deleted_successfully ||
                "Excel file deleted successfully",
            )
          }
        } catch (error) {
          console.error("Delete error:", error)
          message.error(
            error.response?.data?.error ||
              error.response?.data?.message ||
              t?.failed_to_delete_excel ||
              "Failed to delete Excel file",
          )
        } finally {
          setContactsLoading(false)
        }
      },
    })
  }

  const handleSubmitContact = async (values) => {
    if (readOnly) return
    try {
      setContactsLoading(true)

      if (editingContact) {
        const payload = {
          name: values.name,
          email: values.email,
        }
        const updateResponse = await ContactsApi.updateContact(
          editingContact.id,
          payload,
        )

        if (
          updateResponse?.status === 200 ||
          updateResponse?.status === 201 ||
          updateResponse?.data
        ) {
          message.success(
            t?.contact_updated_successfully || "Contact updated successfully",
          )

          // Reset the fetching ref to allow immediate refresh
          isFetchingRef.current = false

          // ALWAYS call GET API immediately after successful update to show latest data
          try {
            const refreshResponse = await ContactsApi.getContactsByContactId(
              contactList.id,
              {
                search: debouncedSearch,
                page: contactPagination.current,
                pageSize: contactPagination.pageSize,
              },
            )

            const responseData = refreshResponse?.data || refreshResponse

            if (responseData?.results && Array.isArray(responseData.results)) {
              const mappedContacts = responseData.results.map((item) => ({
                id: item.id,
                name: item.name || "",
                email: item.email || "",
              }))

              setContactsData(mappedContacts)
              setContactPagination((prev) => ({
                ...prev,
                total: responseData.count || 0,
              }))
            }
          } catch (refreshError) {
            console.error("Error refreshing contacts:", refreshError)
            // Fallback: use fetchContacts function
            await fetchContacts()
          }
        }
      } else {
        // Use createContact API with contact and Excel file
        const payload = {
          contact: contactList.id,
        }

        // Add name and email only if provided (not required when Excel is uploaded)
        if (values.name) {
          payload.name = values.name
        }
        if (values.email) {
          payload.email = values.email
        }

        // Add S3 path if Excel file was already uploaded
        let createResponse = null
        if (uploadedExcelPaths.length > 0) {
          const uploadResponses = []
          for (const filePath of uploadedExcelPaths) {
            uploadResponses.push(
              await ContactsApi.createContact({
                ...payload,
                file_path: filePath,
              }),
            )
          }
          createResponse = uploadResponses[uploadResponses.length - 1]
        } else {
          createResponse = await ContactsApi.createContact(payload)
        }

        if (
          createResponse?.status === 200 ||
          createResponse?.status === 201 ||
          createResponse?.data
        ) {
          message.success(
            t?.contact_added_successfully || "Contact added successfully",
          )
          setUploadedExcelPaths([])
          setExcelFileList([])

          // Reset the fetching ref to allow immediate refresh
          isFetchingRef.current = false

          // ALWAYS call GET API immediately after successful addition to show latest data
          try {
            const refreshResponse = await ContactsApi.getContactsByContactId(
              contactList.id,
              {
                search: debouncedSearch,
                page: contactPagination.current,
                pageSize: contactPagination.pageSize,
              },
            )

            const responseData = refreshResponse?.data || refreshResponse

            if (responseData?.results && Array.isArray(responseData.results)) {
              const mappedContacts = responseData.results.map((item) => ({
                id: item.id,
                name: item.name || "",
                email: item.email || "",
              }))

              setContactsData(mappedContacts)
              setContactPagination((prev) => ({
                ...prev,
                total: responseData.count || 0,
              }))
            }
          } catch (refreshError) {
            console.error("Error refreshing contacts:", refreshError)
            // Fallback: use fetchContacts function
            await fetchContacts()
          }

          // Refresh contact list details to update total count
          if (onContactListUpdate) {
            try {
              const contactListResponse = await ContactsApi.getContactLists({
                page: 1,
                pageSize: 1000, // Get all to find the specific one
              })

              const contactListData =
                contactListResponse?.data || contactListResponse
              if (contactListData?.results) {
                const updatedList = contactListData.results.find(
                  (list) => list.id === contactList.id,
                )
                if (updatedList) {
                  onContactListUpdate({
                    ...contactList,
                    contactsCount:
                      updatedList.total_contact_users ||
                      updatedList.contacts_count ||
                      contactList.contactsCount ||
                      0,
                    excel_file_path:
                      updatedList.excel_file_path ||
                      contactList.excel_file_path,
                    files: Array.isArray(updatedList.files) ? updatedList.files : [],
                  })
                }
              }
            } catch (contactListRefreshError) {
              console.error(
                "Error refreshing contact list:",
                contactListRefreshError,
              )
            }
          }
        }
      }

      setShowAddContactModal(false)
      setEditingContact(null)
      setExcelFileList([])
      form.resetFields()
    } catch (error) {
      console.error("Error saving contact:", error)
      
      // If it's a 400 Bad Request error, clear the uploaded Excel path
      // because the backend has already removed the invalid file from S3
      if (error.response?.status === 400 && uploadedExcelPaths.length > 0) {
        setUploadedExcelPaths([])
        setExcelFileList([])
        // Also clear the form field to remove the file from display
        form.setFieldsValue({ excel_file: undefined })
      }
      
      message.error(
        error.response?.data?.error ||
          t?.error_saving_contact ||
          "Error saving contact",
      )
    } finally {
      setContactsLoading(false)
    }
  }

  const columns = [
    {
      title: t?.detail?.name || "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <div style={{ fontWeight: 500, color: "#101828" }}>{text}</div>
      ),
    },
    {
      title: t?.detail?.email || "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => <div style={{ color: "#667085" }}>{text}</div>,
    },
    {
      title: t?.detail?.actions || "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        if (readOnly) {
          return (
            <span style={{ color: "#8C8C8C" }}>—</span>
          )
        }
        const menuItems = [
          {
            key: "edit",
            label: (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src="/Campaign/Button-edit.svg"
                  alt="Edit"
                  style={{ width: "16px", height: "16px" }}
                />
                {t?.buttons?.edit || "Edit"}
              </span>
            ),
            onClick: () => handleEditContact(record),
          },
          ...(canDelete ? [{
            key: "delete",
            label: (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src="/Campaign/delete-icon.svg"
                  alt="Delete"
                  style={{ width: "16px", height: "16px" }}
                />
                {t?.buttons?.delete || "Delete"}
              </span>
            ),
            onClick: () => {
              Modal.confirm({
                title: t?.confirm_delete_contact || "Confirm Delete Contact",
                content:
                  t?.confirm_delete_contact_message ||
                  "Are you sure you want to delete this contact? This action cannot be undone.",
                okText: t?.modal?.delete || "Delete",
                okType: "danger",
                cancelText: t?.modal?.cancel || "Cancel",
                centered: true,
                onOk: () => handleDeleteContact(record.id),
              })
            },
          }] : []),
        ]

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <MoreOutlined
              className="contact-actions-icon"
              style={{ cursor: "pointer", fontSize: "18px" }}
            />
          </Dropdown>
        )
      },
    },
  ]

  if (loading) {
    return (
      <div className="contact-detail-container">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    )
  }

  if (!contactList) return null

  return (
    <div className="contact-detail-container">
      {/* Breadcrumbs */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "24px",
          color: "#667085",
          fontSize: "14px",
        }}
      >
        <span style={{ cursor: "pointer" }} onClick={onBack}>
          {t?.breadcrumbs?.contacts || "Contacts"}
        </span>
        <img
          src="/discounts/angle-right.svg"
          alt=""
          style={{ width: "16px", height: "16px" }}
        />
        <span style={{ color: "#101828", fontWeight: 500 }}>
          {contactList.name}
        </span>
      </div>

      {/* Contact List Overview Section */}
      <div className="contact-detail-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 0,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "24px",
                fontWeight: 600,
                color: "#101828",
                margin: 0,
                marginBottom: "8px",
              }}
            >
              {contactList.name}
            </h1>
            <p style={{ color: "#4A5565", fontSize: "14px", margin: 0 }}>
              {contactList.description || ""}
            </p>
          </div>
        </div>

        {/* Contact List Attributes */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "32px",
            paddingTop: "24px",
          }}
        >
          <div>
            <div
              style={{
                color: "#8C8C8C",
                fontSize: "14px",
              }}
            >
              {t?.detail?.total_contacts || "Total Contacts"}
            </div>
            <div
              style={{
                color: "#101828",
                fontSize: "15px",
                fontWeight: 500,
              }}
            >
              {contactList.contactsCount || 0}
            </div>
          </div>

          <div>
            <div
              style={{
                color: "#8C8C8C",
                fontSize: "14px",
                marginBottom: "8px",
              }}
            >
              {t?.detail?.excel_file || "Excel File"}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {contactFiles.length > 0 ? (
                <span
                  style={{
                    color: "#101828",
                    fontSize: "14px",
                    fontWeight: 500,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {contactFiles[0]?.file_path?.split("/").pop()}
                  {contactFiles.length > 1 ? ` (+${contactFiles.length - 1})` : ""}
                </span>
              ) : (
                <span
                  style={{
                    color: "#8C8C8C",
                    fontSize: "14px",
                    fontStyle: "italic",
                  }}
                >
                  {t?.no_excel_file || "No Excel file"}
                </span>
              )}
              {contactFiles.length > 0 && (
                <>
                  <Button
                    icon={<DownloadOutlined />}
                    size="small"
                    onClick={() => handleDownloadExcel(contactFiles[0]?.file_path)}
                    style={{
                      border: "1px solid #D0D5DD",
                    }}
                  >
                    {t?.buttons?.download || "Download"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div
        className="contact-detail-card contact-contacts-card"
        style={{ marginBottom: 0 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            paddingLeft: "16px",
            paddingRight: "16px",
            gap: "16px",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#101828",
              margin: 0,
            }}
          >
            {t?.detail?.contacts || "Contacts"}
          </h2>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Input
              placeholder={t?.search_contacts || "Search by name"}
              prefix={<SearchOutlined style={{ color: "#8C8C8C" }} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{
                width: "300px",
                borderRadius: "8px",
                height: "36px",
              }}
              allowClear
            />
            {!readOnly && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddContact}
                style={{
                  backgroundColor: "#3E3A36",
                  color: "#fff",
                  borderRadius: "8px",
                  height: "36px",
                  fontWeight: 500,
                }}
              >
                {t?.add_contact || "Add Contact"}
              </Button>
            )}
          </div>
        </div>

        <div className="contact-detail-table-container">
          <Table
            columns={columns}
            dataSource={contactsData}
            loading={contactsLoading}
            pagination={{
              current: contactPagination.current,
              total: contactPagination.total,
              pageSize: contactPagination.pageSize,
              showSizeChanger: false,
              onChange: (page) => {
                setContactPagination((prev) => ({ ...prev, current: page }))
              },
            }}
            rowKey="id"
          />
        </div>
      </div>

      {/* Update Excel Modal */}
      <Modal
        open={showUpdateExcelModal}
        title={t?.update_excel_file || "Update Excel File"}
        onCancel={() => {
          setShowUpdateExcelModal(false)
          setUploadedExcelPaths([])
          updateExcelForm.resetFields()
        }}
        footer={null}
        width={500}
        centered
      >
        <Form
          form={updateExcelForm}
          layout="vertical"
          onFinish={handleUpdateExcel}
          autoComplete="off"
        >
          <Form.Item
            name="excel_file"
            label={t?.form?.upload_excel || "Upload Excel File"}
            valuePropName="file"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e[0]
              }
              return e?.file
            }}
            rules={[
              {
                required: true,
                message:
                  t?.form?.excel_file_required || "Excel file is required",
              },
            ]}
          >
            <Upload
              accept=".xls,.xlsx"
              maxCount={1}
              beforeUpload={async (file) => {
                const isValidType = [".xls", ".xlsx"].some((ext) =>
                  file.name.toLowerCase().endsWith(ext),
                )
                const isValidSize = file.size / 1024 / 1024 < 10 // 10MB max

                if (!isValidType) {
                  message.error(
                    t?.form?.excel_file_type_error ||
                      "Only Excel files (.xls, .xlsx) are allowed",
                  )
                  return Upload.LIST_IGNORE
                }

                if (!isValidSize) {
                  message.error(
                    t?.form?.excel_file_size_error ||
                      "File size must be less than 10MB",
                  )
                  return Upload.LIST_IGNORE
                }

                // Upload to S3 immediately
                try {
                  setUploadingExcel(true)
                  validateExcelFile(file)

                  const s3Result = await uploadContactExcelToS3(
                    file,
                    contactList.id,
                  )

                  // Store the S3 path
                  setUploadedExcelPaths([s3Result.key])

                  // Store file info in form for display
                  updateExcelForm.setFieldsValue({
                    excel_file: {
                      file: file,
                      name: file.name,
                      status: "done",
                    },
                  })

                  message.success(
                    t?.form?.excel_uploaded_successfully ||
                      "Excel file uploaded successfully",
                  )
                } catch (uploadError) {
                  console.error(
                    "Error uploading Excel file to S3:",
                    uploadError,
                  )
                  message.error(
                    uploadError.message ||
                      t?.error_uploading_excel ||
                      "Error uploading Excel file",
                  )
                  setUploadedExcelPaths([])
                } finally {
                  setUploadingExcel(false)
                }
                return false // Prevent auto-upload
              }}
              onRemove={() => {
                updateExcelForm.setFieldsValue({ excel_file: undefined })
                setUploadedExcelPaths([])
              }}
              showUploadList={{
                showRemoveIcon: true,
                showPreviewIcon: false,
              }}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploadingExcel}
                style={{
                  width: "100%",
                  border: "1px dashed #D0D5DD",
                  borderRadius: "8px",
                  height: "40px",
                }}
              >
                {uploadingExcel
                  ? t?.form?.uploading || "Uploading..."
                  : uploadedExcelPaths.length > 0
                    ? t?.form?.excel_uploaded || "Excel file uploaded"
                    : t?.form?.click_to_upload || "Click to upload Excel file"}
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <Button
                onClick={() => {
                  setShowUpdateExcelModal(false)
                  setUploadedExcelPaths([])
                  updateExcelForm.resetFields()
                }}
              >
                {t?.modal?.cancel || "Cancel"}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={updatingExcel}
                style={{
                  backgroundColor: "#3E3A36",
                  borderColor: "#3E3A36",
                }}
              >
                {t?.buttons?.update || "Update"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add/Edit Contact Modal */}
      <Modal
        open={showAddContactModal}
        title={
          editingContact
            ? t?.edit_contact || "Edit Contact"
            : t?.add_contact || "Add Contact"
        }
        onCancel={() => {
          setShowAddContactModal(false)
          setEditingContact(null)
          setUploadedExcelPaths([])
          setExcelFileList([])
          form.resetFields()
        }}
        footer={null}
        width={500}
        centered
        className="add-contact-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitContact}
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label={t?.form?.name || "Name"}
            rules={[
              {
                required: uploadedExcelPaths.length === 0 && contactFiles.length === 0,
                message: t?.form?.name_required || "Name is required",
              },
            ]}
          >
            <Input
              placeholder={
                t?.form?.contact_name_placeholder || "Enter contact name"
              }
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={t?.form?.email || "Email"}
            rules={[
              {
                validator: (_, value) => {
                  // Check if email is required (when no Excel file is uploaded)
                  const isRequired =
                    uploadedExcelPaths.length === 0 && contactFiles.length === 0

                  // If required and value is empty, show required error
                  if (isRequired && (!value || value.trim() === "")) {
                    return Promise.reject(
                      new Error(t?.form?.email_required || "Email is required"),
                    )
                  }

                  // If value is provided, validate email format
                  if (value && value.trim() !== "") {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                    if (!emailRegex.test(value.trim())) {
                      return Promise.reject(
                        new Error(
                          t?.form?.email_invalid || "Invalid email format",
                        ),
                      )
                    }
                  }

                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input placeholder={t?.form?.email_placeholder || "Enter email"} />
          </Form.Item>

          {!editingContact && contactFiles.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <div
                style={{
                  color: "#8C8C8C",
                  fontSize: "14px",
                  marginBottom: "8px",
                }}
              >
                {t?.existing_excel_file || "Existing Excel File"}
              </div>
              <div
                style={{
                  maxHeight: "260px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: "4px",
                }}
              >
                {contactFiles.map((fileItem) => (
                  <div
                    key={fileItem.id || fileItem.file_path}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px",
                      border: "1px solid #D0D5DD",
                      borderRadius: "8px",
                      backgroundColor: "#F9FAFB",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          color: "#101828",
                          fontSize: "14px",
                          fontWeight: 500,
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={fileItem.file_path?.split("/").pop()}
                      >
                        {fileItem.file_path?.split("/").pop()}
                      </span>
                    </div>
                    <Button
                      icon={<DownloadOutlined />}
                      size="small"
                      onClick={() => handleDownloadExcel(fileItem.file_path)}
                      style={{ border: "1px solid #D0D5DD", marginRight: "8px" }}
                    >
                      {t?.buttons?.download || "Download"}
                    </Button>
                    {!readOnly && canDelete && (
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteExcel(fileItem)}
                        disabled={!fileItem.id}
                        style={{
                          border: "1px solid #F04438",
                        }}
                      >
                        {t?.buttons?.delete || "Delete"}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!editingContact && (
            <Form.Item
              name="excel_file"
              label={t?.form?.upload_excel || "Upload Excel File"}
              valuePropName="file"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e[0]
                }
                return e?.file
              }}
            >
              <Upload
                accept=".xls,.xlsx"
                maxCount={20}
                fileList={excelFileList}
                beforeUpload={async (file) => {
                  const isValidType = [".xls", ".xlsx"].some((ext) =>
                    file.name.toLowerCase().endsWith(ext),
                  )
                  const isValidSize = file.size / 1024 / 1024 < 10 // 10MB max

                  if (!isValidType) {
                    message.error(
                      t?.form?.excel_file_type_error ||
                        "Only Excel files (.xls, .xlsx) are allowed",
                    )
                    return Upload.LIST_IGNORE
                  }

                  if (!isValidSize) {
                    message.error(
                      t?.form?.excel_file_size_error ||
                        "File size must be less than 10MB",
                    )
                    return Upload.LIST_IGNORE
                  }

                  // Upload to S3 immediately
                  try {
                    setUploadingExcel(true)
                    validateExcelFile(file)

                    const s3Result = await uploadContactExcelToS3(
                      file,
                      contactList.id,
                    )

                    // Store the S3 path
                    setUploadedExcelPaths((prev) => [...prev, s3Result.key])

                    // Add file to the controlled file list
                    // Don't include url to prevent showing as link
                    setExcelFileList((prev) => [
                      ...prev,
                      {
                        uid: file.uid,
                        name: file.name,
                        status: "done",
                      },
                    ])

                    // Store file info in form for display
                    form.setFieldsValue({
                      excel_file: {
                        file: file,
                        name: file.name,
                        status: "done",
                      },
                    })

                    message.success(
                      t?.form?.excel_uploaded_successfully ||
                        "Excel file uploaded successfully",
                    )
                  } catch (uploadError) {
                    console.error(
                      "Error uploading Excel file to S3:",
                      uploadError,
                    )
                    message.error(
                      uploadError.message ||
                        t?.error_uploading_excel ||
                        "Error uploading Excel file",
                    )
                    return Upload.LIST_IGNORE
                  } finally {
                    setUploadingExcel(false)
                  }

                  return false // Prevent auto-upload
                }}
                onRemove={() => {
                  form.setFieldsValue({ excel_file: undefined })
                  setUploadedExcelPaths([])
                  setExcelFileList([])
                }}
                showUploadList={{
                  showRemoveIcon: true,
                  showPreviewIcon: false,
                }}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={uploadingExcel}
                  style={{
                    width: "100%",
                    border: "1px dashed #D0D5DD",
                    borderRadius: "8px",
                    height: "40px",
                  }}
                >
                  {uploadingExcel
                    ? t?.form?.uploading || "Uploading..."
                    : uploadedExcelPaths.length > 0
                      ? t?.form?.excel_uploaded || "Excel file uploaded"
                      : t?.form?.click_to_upload ||
                        "Click to upload Excel file"}
                </Button>
              </Upload>
            </Form.Item>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "24px",
            }}
          >
            <Button
              onClick={() => {
                setShowAddContactModal(false)
                setEditingContact(null)
                setUploadedExcelPaths([])
                setExcelFileList([])
                form.resetFields()
              }}
              style={{
                minWidth: "120px",
                height: "46px",
                borderRadius: "50px",
                border: "1px solid #D0D5DD",
                backgroundColor: "#FFFFFF",
                color: "#344054",
              }}
            >
              {t?.modal?.cancel || "Cancel"}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={contactsLoading}
              style={{
                minWidth: "120px",
                height: "46px",
                borderRadius: "50px",
                backgroundColor: "#3E3A36",
                borderColor: "#3E3A36",
              }}
            >
              {editingContact
                ? t?.modal?.update || "Update"
                : t?.modal?.add || "Add"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default ContactDetailView
